import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { wordsApi } from '@/api/words'
import { SectionLabel } from './primitives'
import { cardTestWordSourceOf, type CardTestDelegateContent, type CardTestWordSource } from '../contentTypes'
import type { ThemeWord } from '../themeWords'

/**
 * Редактор задания-делегата: тест по карточкам.
 *
 * Своего содержимого у него нет — задание указывает, из каких слов собрать
 * тренажёр карточек. По умолчанию это правило «слова темы, в которой задание
 * выдано»: одно задание банка стоит сразу в нескольких темах, и жёсткий список
 * там ничего не значил — правка словаря темы до теста не доезжала.
 *
 * Явный список остаётся переопределением «хочу именно эти слова». В банке темы
 * нет, поэтому слова для него ищутся в словаре учителя, а не в словаре темы:
 * раньше список вариантов был пуст и учитель упирался в тупиковое
 * предупреждение «В теме нет словаря».
 */

type EditorProps<T> = {
  content: T
  onChange: (content: T) => void
  disabled?: boolean
  themeWords?: ThemeWord[]
}

/** Сколько слов словаря показывать в выпадающем списке за один поиск */
const SEARCH_LIMIT = 30

export function CardTestDelegateEditor({
  content,
  onChange,
  disabled,
  themeWords = [],
}: EditorProps<CardTestDelegateContent>) {
  const { t } = useTranslation()

  const source = cardTestWordSourceOf(content)
  const wordIds = useMemo(() => content.word_ids ?? [], [content.word_ids])

  const [query, setQuery] = useState('')
  const [options, setOptions] = useState<ThemeWord[]>(themeWords)
  const [loading, setLoading] = useState(false)

  // Подписи выбранных слов копятся отдельно от вариантов поиска: выбранное
  // слово не обязано находиться текущим запросом, а показывать вместо него
  // голый id нельзя.
  const [known, setKnown] = useState<Record<number, ThemeWord>>({})
  const knownRef = useRef(known)
  knownRef.current = known

  const remember = useCallback((words: ThemeWord[]) => {
    if (words.length === 0) return

    setKnown((previous) => {
      const next = { ...previous }
      for (const word of words) next[word.id] = word
      return next
    })
  }, [])

  useEffect(() => {
    remember(themeWords)
  }, [themeWords, remember])

  // Поиск по словарю учителя с задержкой: без неё каждый символ уходил бы
  // отдельным запросом.
  useEffect(() => {
    if (source !== 'explicit' || disabled) return

    let cancelled = false
    const handle = window.setTimeout(() => {
      setLoading(true)
      wordsApi
        .getWords({ search: query.trim() || undefined, per_page: SEARCH_LIMIT })
        .then((response) => {
          if (cancelled) return
          const found = response.data
            .filter((word) => typeof word.id === 'number')
            .map((word) => ({
              id: word.id as number,
              text: word.word ?? '',
              translation: word.translation ?? null,
            }))
          setOptions(found)
          remember(found)
        })
        .catch(() => {
          // Словарь не ответил — оставляем то, что уже показано: пустой список
          // вариантов выглядел бы как «слов нет», а их просто не донесли.
          if (!cancelled) setOptions((previous) => previous)
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }, 300)

    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [query, source, disabled, remember])

  // Подписи для уже выбранных слов, которых нет ни в теме, ни в текущем
  // поиске: задание могли собрать в другой теме или в прошлой сессии.
  useEffect(() => {
    if (source !== 'explicit') return

    const missing = wordIds.filter((id) => !knownRef.current[id])
    if (missing.length === 0) return

    let cancelled = false

    Promise.all(
      missing.map((id) =>
        wordsApi
          .getWord(id)
          .then((word) => ({ id, text: word.word ?? '', translation: word.translation ?? null }))
          .catch(() => null),
      ),
    ).then((loaded) => {
      if (cancelled) return
      remember(loaded.filter((word): word is ThemeWord => word !== null))
    })

    return () => {
      cancelled = true
    }
  }, [wordIds, source, remember])

  const selected = useMemo(
    () => wordIds.map((id) => known[id] ?? { id, text: `#${id}`, translation: null }),
    [wordIds, known],
  )

  const changeSource = (next: CardTestWordSource) => {
    // Выбранные слова не стираем при переключении на тему: учитель может
    // вернуться обратно, и заново набирать список было бы обидно.
    onChange({ ...content, word_source: next })
  }

  return (
    <Stack spacing={2}>
      <Box>
        <SectionLabel text={t('exercises.editor.cardTestSource')} />
        <RadioGroup
          value={source}
          onChange={(event) => changeSource(event.target.value as CardTestWordSource)}
        >
          <FormControlLabel
            value="theme"
            disabled={disabled}
            control={<Radio size="small" />}
            label={t('exercises.editor.cardTestSourceTheme')}
          />
          <FormControlLabel
            value="explicit"
            disabled={disabled}
            control={<Radio size="small" />}
            label={t('exercises.editor.cardTestSourceExplicit')}
          />
        </RadioGroup>
      </Box>

      {source === 'theme' ? (
        <Stack spacing={1}>
          <Alert severity="info">{t('exercises.editor.cardTestSourceThemeHint')}</Alert>

          {themeWords.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                {t('exercises.editor.cardTestThemePreview', { count: themeWords.length })}
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                {themeWords.map((word) => (
                  <Chip key={word.id} size="small" variant="outlined" label={word.text} />
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      ) : (
        <Box>
          <SectionLabel
            text={t('exercises.editor.cardTestWords')}
            hint={t('exercises.editor.cardTestWordsHint')}
          />
          <Autocomplete
            multiple
            filterSelectedOptions
            options={options}
            value={selected}
            disabled={disabled}
            loading={loading}
            // Фильтрует сервер: список слов учителя не помещается в память
            // страницы, и локальный фильтр показывал бы только первую выборку.
            filterOptions={(all) => all}
            noOptionsText={t('exercises.editor.cardTestNoWordsFound')}
            getOptionLabel={(option) => option.text}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onInputChange={(_, value, reason) => {
              if (reason === 'input') setQuery(value)
            }}
            onChange={(_, values) => {
              onChange({ ...content, word_source: 'explicit', word_ids: values.map((word) => word.id) })
            }}
            renderTags={(values, getTagProps) =>
              values.map((value, index) => (
                <Chip size="small" label={value.text} {...getTagProps({ index })} key={value.id} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label={t('exercises.editor.words')}
                placeholder={t('exercises.editor.cardTestWordsPlaceholder')}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
          />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary">
        {t('exercises.editor.cardTestDelegateHint')}
      </Typography>
    </Stack>
  )
}
